import PropTypes from 'prop-types'
import { useState, useEffect, useMemo } from 'react'
import Paper from 'components/Paper'
import Type from 'components/Type'
import Label from 'components/Label'
import Rotate from 'components/State/Rotate'
import MuiMenu from '@mui/material/Menu'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { IconChevronDown } from 'icons'
import { isNil, find, flatten } from 'lodash'

const VARIANTS = {
  default: {
    color: 'text.neutralCool_700',
    paperProps: {},
  },
  inverse: {
    color: 'text.inverse',
    paperProps: {
      variant: 'inverse',
      variantActive: 'neutral_1000',
      borderWidth: 'main',
    },
  },
}

const Menu = props => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [width, setWidth] = useState('auto')
  const open = Boolean(anchorEl)
  const {
    placeholder,
    color,
    colorActive,
    bgVariant,
    bgVariantActive,
    borderColor,
    borderWidth,
    label,
    paperProps,
    padding,
    onOpen,
    onClose,
    flip,
    children,
    variant,
    ...otherProps
  } = props
  const activePlaceholder = useMemo(() => {
    const temp = find(flatten(children), o => o?.props?.value === otherProps.value)
    return isNil(temp?.props?.children) ? placeholder : temp?.props?.children
  }, [children, otherProps.value, placeholder])
  const menuVariant = useMemo(() => VARIANTS[variant], [variant])
  
  const handleClick = e => {
    setAnchorEl(e.currentTarget)
    setWidth(e.currentTarget.offsetWidth)
    onOpen()
  }
  const handleClose = () => {
    onClose()
    if (typeof props.open !== 'boolean') setAnchorEl(null)
  }

  useEffect(() => {
    if (!props.open) setAnchorEl(null)
  }, [props.open])

  return (
    <Stack
      gap={1}
    >
      {label && (
        <Label>
          {label}
        </Label>
      )}
      <Paper
        className="menu"
        outlined
        round="more"
        variant="neutralCool_75"
        selectable
        onClick={handleClick}
        {...menuVariant.paperProps}
        {...paperProps}
      >
        <Stack
          display="grid"
          gridTemplateColumns="1fr auto"
          gap={1}
          alignItems="center"
          alignContent="center"
          px={1.5}
          py={1}
          color={menuVariant.color}
        >
          <Type
            variant="data"
          >
            {activePlaceholder}
          </Type>
          <Rotate active={open}>
            <Box
              display="flex"
            >
              <IconChevronDown />
            </Box>
          </Rotate>
        </Stack>
      </Paper>
      <MuiMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: flip ? 'top' : 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: flip ? 'bottom' : 'top',
          horizontal: 'left',
        }}
        MenuListProps={{
          sx: {
            width,
            p: padding,
          },
        }}
      >
        {children}
      </MuiMenu>
    </Stack>
  )
}

Menu.propTypes = {
  padding: PropTypes.number,
  paperProps: PropTypes.any,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  flip: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'inverse']),
}

Menu.defaultProps = {
  paperProps: {},
  onOpen: () => {},
  onClose: () => {},
  variant: 'default',
}

export default Menu